import React, {useEffect, useState} from 'react';
import {AiFillCheckCircle, AiFillClockCircle} from "react-icons/ai";

import classes from './ScheduleGrid.module.scss';

import {
    getAllLessonsPeriods,
    getNextLessonToEnd,
    getWeekFilterSelectionInfo,
    ifGrayOutScheduleItem,
    lessonIsAnnouncement,
    lessonIsExam,
    lessonIsLesson,
    sortLessonPeriods,
    sortScheduleLessons
} from '../../../services/scheduleService';
import {getWeekDayName, stringToDateTime} from '../../../services/generalService';

import ScheduleGridItem from './ScheduleGridItem/ScheduleGridItem';
import AnnouncementGridItem from '../Announcement/AnnouncementGridItem/AnnouncementGridItem';
import useDefaultWeek from "../ScheduleFilters/useDefaultWeek";
import {useSelector} from "react-redux";
import {getDateLabel, getDateOfWeekdayOptions} from "../CommonSchedule/common";

const ScheduleGrid = ({ scheduleData, scheduleItemType, scheduleType, startOfTermDate,
                        isCurrentPeriod }) => {
    const [days] = useState(['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'])
    const [rerenderTimeoutId, setRerenderTimeoutId] = useState(null)

    const currentWeek = useSelector(state => state.schedule.currentWeekNumber);
    const defaultWeek = useDefaultWeek(startOfTermDate);

    const weekFilters = useSelector(state => state.schedule.weekFilters);

    const weekFilterSelection = getWeekFilterSelectionInfo(weekFilters, currentWeek, defaultWeek);
    const dateOfWeekdayOptions = getDateOfWeekdayOptions(weekFilterSelection, startOfTermDate, isCurrentPeriod);
    const dateOfWeekdayOption = dateOfWeekdayOptions.find(o => o.shouldApply);

    const schedule = sortScheduleLessons(Object.entries(scheduleData ?? {}));

    useEffect(() => { // Написано с расчётом на таймер для ежедневного обновления
        const periods = sortLessonPeriods(getAllLessonsPeriods(schedule));
        let timeoutId = null;

        const currentDate = new Date();
        const currentTime = currentDate.getTime();
        const currentPeriod = getCurrentLessonPeriod(periods, currentTime);

        const currentDay = currentDate.getDay();
        const currentDaySchedule = schedule.find(daySchedule => daySchedule[0] === days[currentDay]);
        const nextLessonToEnd = currentDaySchedule ? getNextLessonToEnd(currentDaySchedule[1], currentTime) : null;

        if (currentPeriod || nextLessonToEnd) {
            let periodWithClosestEnd;
            if (!currentPeriod) {
                periodWithClosestEnd = nextLessonToEnd;
            } else if (!nextLessonToEnd) {
                periodWithClosestEnd = currentPeriod;
            } else {
                periodWithClosestEnd = currentPeriod.endLessonTime < nextLessonToEnd.endLessonTime ? currentPeriod : nextLessonToEnd;
            }

            const nextRerenderTime = stringToDateTime(periodWithClosestEnd.endLessonTime).getTime() + 60*1000;//1 минута
            timeoutId = setTimeout(() => {
                setRerenderTimeoutId(timeoutId);
            }, nextRerenderTime - currentTime);
        }

        return () => clearTimeout(timeoutId);
    }, [rerenderTimeoutId, schedule, days])

    const getCurrentLessonPeriod = (periods, currentTime) => {
        return periods.find(candidatePeriod => currentTime <= stringToDateTime(candidatePeriod.endLessonTime).getTime());
    }

    const setDateLabel = (dayName, dayIndex) => {
        const currentDay = dateOfWeekdayOption?.getDate(dayIndex + 1);
        if (!currentDay) return null;

        return <div>{getDateLabel(currentDay)}</div>
    }

    const setTableHeader = () => {
        const dayNames = [...days]
        dayNames.shift()
        const columnHeaders = []
        const currentDayName = days[new Date().getDay()]

        for (let i = 0; i < dayNames.length; i++) {
            columnHeaders.push(<th
                key={`th${i}`}
                width="16%"
                className={currentDayName === dayNames[i] ? 'ScheduleGrid_currentDayHeading' : ''}>
                <div className={classes.dayOfWeek}>
                    {dayNames[i]}
                </div>
                <div>
                    {setDateLabel(dayNames[i], i)}
                </div>
            </th>)
        }

        return columnHeaders
    }

    const setScheduleGridRow = (periods) => {
        const rows = []
        const currentTime = (new Date()).getTime()

        const firstPeriod = periods.reduce((firstPeriodToStart, period) => (!firstPeriodToStart || period.startLessonTime < firstPeriodToStart.startLessonTime) ? period : firstPeriodToStart, null)
        const lastPeriod = periods.reduce((lastPeriodToStart, period) => (!lastPeriodToStart || period.startLessonTime > lastPeriodToStart.startLessonTime) ? period : lastPeriodToStart, null)

        const announcementLessonBelongsToPeriod = (announcementLesson, period, nextPeriod) => {
            return (period === firstPeriod && announcementLesson.startLessonTime < firstPeriod.startLessonTime)
                || (announcementLesson.startLessonTime >= period.startLessonTime && announcementLesson.startLessonTime < period.endLessonTime)
                || (period.endLessonTime <= announcementLesson.startLessonTime && announcementLesson.startLessonTime < nextPeriod?.startLessonTime)
                || (period === lastPeriod && announcementLesson.startLessonTime > lastPeriod.endLessonTime)
        }

        const examLessonBelongsToPeriod = (examLesson, period, nextPeriod) => {
            return announcementLessonBelongsToPeriod(examLesson, period, nextPeriod)
        }

        const currentPeriod = getCurrentLessonPeriod(periods, currentTime)

        periods.forEach((period, periodIndex) => {
            const nextPeriod = (periodIndex < periods.length - 1) ? periods[periodIndex + 1] : null

            const periodIsCurrent = period === currentPeriod
            const row = []
            schedule.forEach(([day, lessons]) => {
                const rowDayItems = []
                //Предполагается, что lessons отсортированы. "Активная" пара может находиться и после "активной" ячейки
                const mostActualLesson = lessons.find(lesson => lessonIsLesson(lesson) && currentTime <= stringToDateTime(lesson.endLessonTime).getTime())
                lessons.forEach(lesson => {
                    if (lessonIsLesson(lesson) && lesson.startLessonTime === period.startLessonTime) {
                        rowDayItems.push({...lesson, toBeHighlighted: lesson.endLessonTime === mostActualLesson?.endLessonTime})
                    } else if (lessonIsAnnouncement(lesson) && (announcementLessonBelongsToPeriod(lesson, period, nextPeriod))) {
                        rowDayItems.push({...lesson, toBeHighlighted: false})
                    } else if (lessonIsExam(lesson) && (examLessonBelongsToPeriod(lesson, period, nextPeriod))) {
                        const startExamTime = stringToDateTime(lesson.startLessonTime).getTime()
                        const endExamTime = stringToDateTime(lesson.endLessonTime).getTime()
                        const endPeriodTime = stringToDateTime(period.endLessonTime).getTime()
                        rowDayItems.push({...lesson, toBeHighlighted: (periodIsCurrent && endPeriodTime <= currentTime) || (startExamTime <= currentTime && currentTime <= endExamTime)})
                    }
                })
                row.push({ day: day, lessonsArray: rowDayItems })
            })
            rows.push(mapWithEmptyDays(row))
        });

        return rows
    }

    const mapWithEmptyDays = (rows) => {
        const weekDays = [...days]
        weekDays.shift()

        const result = []
        for (const day of weekDays) {
            let ifEqual = false
            let index = null
            for (let j = 0; j < rows.length; j++) {
                if (day === rows[j].day) { ifEqual = true; index = j }
            }
            if (ifEqual) {
                result.push(rows[index])
            } else {
                result.push({ day: day, lessonsArray: [] })
            }
        }

        return result
    }

    const setTimeMarkCell = (periods, indexRow, isActualPeriod) => {
        let startTimeIcon
        const currentTime = (new Date()).getTime()
        if (isActualPeriod) startTimeIcon = <AiFillClockCircle color='#1890ff' size={15} />
        else if (stringToDateTime(periods[indexRow].startLessonTime).getTime() < currentTime) startTimeIcon = <AiFillCheckCircle color='green' size={15} />
        else startTimeIcon = <svg viewBox="0 0 110 110">
            <circle cx="58" cy="55" r="45" stroke="gray" strokeWidth="15" fill="white" />
        </svg>

        let endTimeIcon
        if (isActualPeriod) endTimeIcon = <svg viewBox="0 0 110 110">
                <circle cx="58" cy="55" r="45" stroke="#1890ff" strokeWidth="15" fill="white" />
            </svg>
        else if (stringToDateTime(periods[indexRow].endLessonTime).getTime() < currentTime) endTimeIcon = <AiFillCheckCircle color='green' size={15} />
        else endTimeIcon = <svg viewBox="0 0 110 110">
            <circle cx="58" cy="55" r="45" stroke="gray" strokeWidth="15" fill="white" />
        </svg>

        return <td>
            <div>
                <span>{periods[indexRow].startLessonTime}</span>
                <span className={classes.timeMark}>
                    {startTimeIcon}
                </span>
            </div>
            <div>
                <span>{periods[indexRow].endLessonTime}</span>
                <span className={classes.timeMark}>
                    {endTimeIcon}
                </span>
            </div>
        </td>
    }

    const setEmptyCell = (dayName, day, indexDay, lessonPeriodIsCurrent) => {
        let cellClass
        if (day.day === dayName && lessonPeriodIsCurrent) {
            cellClass = `ScheduleGrid_emptyTimeCell_color ${classes.emptyTimeCell} ScheduleGrid_higlightCurrentTimeDayCell`
        } else {
            if (day.day === dayName) cellClass = `ScheduleGrid_emptyTimeCell_color ${classes.emptyTimeCell} ScheduleGrid_highlightCurrentDayOrTime`
            else cellClass = `ScheduleGrid_emptyTimeCell_color ${classes.emptyTimeCell}`
        }

        return <td className={cellClass} key={indexDay}>
            <div>Занятий нет</div>
        </td>
    }

    const setLessonCell = (dayName, day, indexDay, lessonPeriodIsCurrent) => {
        let cellClass = ''
        if (day.day === dayName && lessonPeriodIsCurrent) {
            cellClass = 'ScheduleGrid_higlightCurrentTimeDayCell'
        } else {
            if (day.day === dayName) cellClass = 'ScheduleGrid_highlightCurrentDayOrTime'
        }

        return <td key={indexDay} className={cellClass} style={{ minWidth: '180px' }}>
            {
                day.lessonsArray.map((lesson, indexLesson) => {
                    const shouldGrayOut = ifGrayOutScheduleItem(lesson, weekFilterSelection.onlyDefaultWeekSelected, isCurrentPeriod);
                    if (!lessonIsAnnouncement(lesson))
                        return <ScheduleGridItem
                            key={indexLesson}
                            lesson={lesson}
                            scheduleItemType={lesson.dateLesson ? "examLesson" : scheduleItemType}
                            scheduleType={scheduleType}
                            dayName={day.day}
                            lessonPeriodIsCurrent={lesson.toBeHighlighted}
                            shouldGrayOut={shouldGrayOut}/>
                    else return <AnnouncementGridItem
                        key={`announcement${indexLesson}`} 
                        scheduleType={scheduleType}
                        note={lesson.note}
                        date={lesson.startLessonDate}
                        startLessonTime={lesson.startLessonTime}
                        endLessonTime={lesson.endLessonTime}
                        employees={lesson.employees}
                        studentGroups={lesson.studentGroups}
                        auditory={lesson.auditories}
                        shouldGrayOut={shouldGrayOut}/>
                })
            }
        </td>
    }

    const mapGridRowsToJSX = () => {
        const periods = sortLessonPeriods(getAllLessonsPeriods(schedule))
        const rows = setScheduleGridRow(periods)
        let rowsJSX
        const dayName = getWeekDayName(new Date())

        rowsJSX = rows.map((row, indexRow) => {
            const currentRowToBeHighlighted = highlightCurrentTimeRow(periods[indexRow], periods);

            return <tr key={indexRow}  className={currentRowToBeHighlighted ? 'ScheduleGrid_highlightCurrentDayOrTime' : ''}>
                {setTimeMarkCell(periods, indexRow, currentRowToBeHighlighted)}

                {row.map((day, indexDay) => {
                    if (day.lessonsArray.length === 0 || (dateOfWeekdayOption && !dateOfWeekdayOption.shouldDisplayDayLessons(indexDay + 1))) {
                        return setEmptyCell(dayName, day, indexDay, currentRowToBeHighlighted)
                    } else {
                        return setLessonCell(dayName, day, indexDay, currentRowToBeHighlighted)
                    }  
                }
                )}
            </tr>
        })
        return rowsJSX
    }

    const highlightCurrentTimeRow = (period, periods) => {
        const currentTime = (new Date()).getTime()
        // Работает без сортировки внутри этого метода, так как periods уже отсортирован ранее
        return period === getCurrentLessonPeriod(periods, currentTime);
    }

    return (
        <div className={`${classes.container} ScheduleGrid_container_color`}>
            <table className={classes.tableContainer}>
                <thead>
                    <tr>
                        <th/>
                        {setTableHeader()}
                    </tr>
                </thead>
                <tbody>
                    {mapGridRowsToJSX()}
                </tbody>
            </table>
        </div>
    )
}

export default ScheduleGrid;
